import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { motion } from 'framer-motion';
import { Coins, ToggleLeft, ToggleRight } from 'lucide-react';

const CurrencyNode = ({ data }) => {
  const { currencyType, name, isMintable, onUpdate } = data;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-slate-800 rounded-xl border-2 border-indigo-500/50 shadow-xl min-w-[320px]"
    >
      {/* Header */}
      <div className={`bg-gradient-to-r ${currencyType?.color || 'from-indigo-500 to-purple-600'} rounded-t-lg px-4 py-3`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <Coins className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">Main Currency</h3>
            <p className="text-white/70 text-xs">{currencyType?.title || 'Currency'}</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Currency Name */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1.5">
            Currency Name *
          </label>
          <input
            type="text"
            value={name || ''}
            onChange={(e) => onUpdate?.({ name: e.target.value })}
            placeholder="Enter currency name (must match VerusID)"
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500"
          />
          <p className="text-xs text-slate-500 mt-1">Must match an existing VerusID you control</p>
        </div>

        {/* Mintable toggle for tokens */}
        {currencyType?.id === 'token' && (
          <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
            <div>
              <p className="text-sm font-medium text-white">Mintable Token</p>
              <p className="text-xs text-slate-400">Allow minting/burning by ID controller</p>
            </div>
            <button
              onClick={() => onUpdate?.({ isMintable: !isMintable })}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isMintable 
                  ? 'bg-indigo-500/20 text-indigo-400' 
                  : 'bg-slate-700 text-slate-400'
              }`}
            >
              {isMintable ? (
                <>
                  <ToggleRight className="w-4 h-4" />
                  Yes
                </>
              ) : (
                <>
                  <ToggleLeft className="w-4 h-4" />
                  No
                </>
              )}
            </button>
          </div>
        )}

        {/* Initial Supply for basket */}
        {currencyType?.id === 'basket' && (
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              Initial Supply
            </label>
            <input
              type="number"
              value={data.initialsupply || 100000}
              onChange={(e) => onUpdate?.({ initialsupply: parseFloat(e.target.value) })}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            />
          </div>
        )}

        {/* Gateway converter name for PBaaS */}
        {currencyType?.id === 'pbaas' && (
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              Gateway Converter Name
            </label>
            <input
              type="text"
              value={data.gatewayconvertername || ''}
              onChange={(e) => onUpdate?.({ gatewayconvertername: e.target.value })}
              placeholder="e.g., Bridge"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            />
            <p className="text-xs text-slate-500 mt-1">Required for cross-chain conversions</p>
          </div>
        )}

        {/* Options display */}
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-slate-700 text-slate-300">
            options: {currencyType?.options || 32}
          </span>
          <span className="px-2 py-1 rounded bg-slate-700 text-slate-300">
            proofprotocol: {currencyType?.id === 'token' ? (isMintable ? 2 : 1) : (currencyType?.id === 'erc20' ? 3 : 1)}
          </span>
        </div>
      </div>

      {/* Handles for connections */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-indigo-500 !border-2 !border-slate-800"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-indigo-500 !border-2 !border-slate-800"
      />
    </motion.div>
  );
};

export default CurrencyNode;
